import { AfterViewInit, Component, EventEmitter, Output, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { DirectorService } from '../../../services/director.service';
import { DirectorFlat } from '../../../models/director';
import { DirectorsSearchRequest } from '../../../models/api/directorsSearchRequest';
import { TextNormalizeDirective } from '../../../directives/text-normalize.directive';

@Component({
  selector: 'app-add-connection-director-search',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatSortModule,
    MatInputModule,
    MatPaginatorModule,
    MatFormFieldModule,
    MatIconModule,
    MatTooltipModule,
    MatButtonModule,
    NgxSpinnerModule,
    FormsModule,
    TextNormalizeDirective
  ],
  templateUrl: './add-connection-director-search.component.html',
  styleUrls: ['./add-connection-director-search.component.scss']
})
export class AddConnectionDirectorSearchComponent implements AfterViewInit {
  @Output() directorSelected = new EventEmitter<DirectorFlat | null>();

  private directorService = inject(DirectorService);
  private spinnerService = inject(NgxSpinnerService);
  private router = inject(Router);

  dataSource = new MatTableDataSource<DirectorFlat>([]);
  displayedColumns: string[] = ['actions', 'code', 'fullName', 'yearOfBirth', 'nationalityName', 'professionName'];
  pageSize: number = 5;
  selectedDirector: DirectorFlat | null = null;

  @ViewChild(MatSort, { static: false }) sort!: MatSort;
  @ViewChild(MatPaginator, { static: false }) paginator!: MatPaginator;

  searchForm = {
    fullName: ''
  };

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  onSearch(): void {
    this.searchDirectors();
  }

  searchDirectors(): void {
    if (!this.searchForm.fullName || this.searchForm.fullName.trim() === '') {
      return;
    }

    const request: DirectorsSearchRequest = {
      fullName: this.searchForm.fullName,
      pageNumber: 1,
      pageSize: 100,
      sortBy: 'FullName',
      ascending: true
    };

    this.spinnerService.show();
    this.directorService.searchDirectors(request).subscribe({
      next: (response) => {
        this.dataSource.data = response.data;
        if (this.paginator) {
          this.paginator.firstPage();
        }
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error searching directors:', error);
        this.spinnerService.hide();
      }
    });
  }

  onConnect(director: DirectorFlat): void {
    // Toggle selection: if clicking the same row, deselect it
    if (this.selectedDirector?.id === director.id) {
      this.selectedDirector = null;
    } else {
      this.selectedDirector = director;
    }
    this.directorSelected.emit(this.selectedDirector);
  }

  isSelected(director: DirectorFlat): boolean {
    return this.selectedDirector?.id === director.id;
  }

  /**
   * Same aid as the Ownership tab's shareholder search: directors with the same or a similar name, or
   * sitting on several boards, cannot be told apart from this list alone, so the record can be opened
   * alongside the dialog to check the CV before connecting.
   */
  onOpenInNewTab(director: DirectorFlat, event: MouseEvent): void {
    event.stopPropagation();

    const url = this.router.serializeUrl(this.router.createUrlTree(['/directors/edit', director.id]));
    window.open(url, '_blank');
  }
}
